import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type {
	CommitmentMatrixAssignmentPerson,
	CommitmentMatrixAssignmentSortColumn,
	CommitmentMatrixAssignmentSortDirection,
} from '@/types'

export interface AssignmentSortState {
	column: CommitmentMatrixAssignmentSortColumn
	direction: CommitmentMatrixAssignmentSortDirection
}

const ORIGIN_LABELS: Record<string, string> = {
	PERSON: 'Pessoal',
	CR: 'CR',
	GROUP: 'Grupo',
	BRANCH: 'Filial',
}

const UNASSIGNED = '__unassigned__'

interface MatrixOption {
	value: string
	label: string
}

interface AssignmentPeopleTableProps {
	people: CommitmentMatrixAssignmentPerson[]
	isLoading: boolean
	isFetching: boolean
	sort: AssignmentSortState
	onSort: (column: CommitmentMatrixAssignmentSortColumn) => void
	matrixOptions: MatrixOption[]
	onAssign: (personId: string, value: string) => void
	onToggleExempt: (
		person: CommitmentMatrixAssignmentPerson,
		exempt: boolean,
	) => void
}

export function AssignmentPeopleTable({
	people,
	isLoading,
	isFetching,
	sort,
	onSort,
	matrixOptions,
	onAssign,
	onToggleExempt,
}: AssignmentPeopleTableProps) {
	return (
		<div className="relative rounded-lg border border-kaefer-light-silver overflow-x-auto">
			{isFetching && !isLoading && (
				<div className="absolute inset-0 z-10 flex items-start justify-center bg-white/60 pt-10">
					<Loader2 className="size-5 animate-spin text-kaefer-medium-grey" />
				</div>
			)}
			<Table
				className={cn(
					'min-w-[720px]',
					isFetching && !isLoading && 'pointer-events-none opacity-50',
				)}
			>
				<TableHeader>
					<TableRow className="bg-muted/30">
						<SortableHead
							column="name"
							sort={sort}
							onSort={onSort}
							className="w-48"
						>
							Colaborador
						</SortableHead>
						<SortableHead
							column="jobRole"
							sort={sort}
							onSort={onSort}
							className="w-32"
						>
							Cargo
						</SortableHead>
						<SortableHead
							column="crName"
							sort={sort}
							onSort={onSort}
							className="w-36"
						>
							CR
						</SortableHead>
						<SortableHead
							column="branchName"
							sort={sort}
							onSort={onSort}
							className="w-28"
						>
							Filial
						</SortableHead>
						<SortableHead
							column="effectiveMatrixName"
							sort={sort}
							onSort={onSort}
							className="w-44"
						>
							Matriz aplicada (efetiva)
						</SortableHead>
						<SortableHead
							column="hasDirectAssignment"
							sort={sort}
							onSort={onSort}
							className="w-40"
						>
							Atribuição direta
						</SortableHead>
						<SortableHead
							column="matrixExempt"
							sort={sort}
							onSort={onSort}
							className="w-20"
						>
							Isento
						</SortableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading ? (
						Array.from({ length: 5 }).map((_, i) => (
							<TableRow key={`skeleton-${i}`}>
								{Array.from({ length: 8 }).map((__, j) => (
									<TableCell key={`skeleton-cell-${j}`}>
										<Skeleton className="h-4 w-full" />
									</TableCell>
								))}
							</TableRow>
						))
					) : people.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={8}
								className="py-10 text-center text-sm text-muted-foreground whitespace-normal"
							>
								Nenhum colaborador encontrado para os filtros selecionados.
							</TableCell>
						</TableRow>
					) : (
						people.map((person) => {
							const { matrix: effectiveMatrixInfo, origin } =
								person.effectiveMatrix

							return (
								<TableRow key={person.id}>
									<TableCell
										className="max-w-48 truncate font-medium text-kaefer-dark-grey"
										title={person.name}
									>
										{person.name}
									</TableCell>
									<TableCell
										className="max-w-32 truncate text-muted-foreground"
										title={person.jobRole ?? undefined}
									>
										{person.jobRole ?? '—'}
									</TableCell>
									<TableCell
										className="max-w-36 truncate text-muted-foreground"
										title={person.crName}
									>
										{person.crName}
									</TableCell>
									<TableCell
										className="max-w-28 truncate text-muted-foreground"
										title={person.branchName}
									>
										{person.branchName}
									</TableCell>
									<TableCell className="max-w-44">
										{person.matrixExempt ? (
											<span className="text-muted-foreground">
												Isento de matriz
											</span>
										) : effectiveMatrixInfo && origin ? (
											<div className="flex flex-col">
												<span
													className="truncate font-medium text-kaefer-dark-grey"
													title={effectiveMatrixInfo.name}
												>
													{effectiveMatrixInfo.name}
												</span>
												<span className="text-xs text-muted-foreground">
													{person.progress.target} insp./mês ·{' '}
													{ORIGIN_LABELS[origin]}
												</span>
											</div>
										) : (
											<span className="text-muted-foreground">
												Sem meta definida
											</span>
										)}
									</TableCell>
									<TableCell>
										<SearchableSelect
											options={matrixOptions}
											value={person.assignedMatrixId ?? UNASSIGNED}
											onValueChange={(v) =>
												onAssign(person.id, v || UNASSIGNED)
											}
											placeholder="Sem atribuição direta"
											searchPlaceholder="Buscar matriz..."
											emptyMessage="Nenhuma matriz encontrada"
											className="w-40"
											disabled={person.matrixExempt}
										/>
									</TableCell>
									<TableCell>
										<Switch
											checked={person.matrixExempt}
											onCheckedChange={(checked) =>
												onToggleExempt(person, checked)
											}
											aria-label={`Isentar ${person.name} de matriz de comprometimento`}
										/>
									</TableCell>
								</TableRow>
							)
						})
					)}
				</TableBody>
			</Table>
		</div>
	)
}

function SortableHead({
	column,
	sort,
	onSort,
	className,
	children,
}: {
	column: CommitmentMatrixAssignmentSortColumn
	sort: AssignmentSortState
	onSort: (column: CommitmentMatrixAssignmentSortColumn) => void
	className?: string
	children: React.ReactNode
}) {
	const isActive = sort.column === column
	const Icon = !isActive
		? ArrowUpDown
		: sort.direction === 'asc'
			? ArrowUp
			: ArrowDown

	return (
		<TableHead className={className}>
			<button
				type="button"
				onClick={() => onSort(column)}
				className={cn(
					'inline-flex items-center gap-1 hover:text-kaefer-dark-grey',
					isActive && 'text-kaefer-dark-grey',
				)}
			>
				<span>{children}</span>
				<Icon className="size-3.5" />
			</button>
		</TableHead>
	)
}
